<script setup>
import { computed } from "vue";

import BrandWordmark from "./BrandWordmark.vue";
import LanguageSwitcher from "./LanguageSwitcher.vue";

const props = defineProps({
  canceledMessage: {
    type: String,
    default: "",
  },
  currentLang: {
    type: String,
    required: true,
  },
  hasUtrField: {
    type: Boolean,
    default: false,
  },
  langLabel: {
    type: Function,
    required: true,
  },
  paidMessage: {
    type: String,
    default: "",
  },
  popupState: {
    type: String,
    required: true,
  },
  processedMessage: {
    type: String,
    required: true,
  },
  supportedLangs: {
    type: Array,
    required: true,
  },
  t: {
    type: Function,
    required: true,
  },
});

defineEmits(["return", "retry", "select-language"]);

const isNegative = computed(() =>
  ["expired", "canceled", "error"].includes(props.popupState),
);

const isError = computed(() => props.popupState === "error");

const sceneTitle = computed(() => {
  if (props.popupState === "paid") return props.t("paymentPaid");
  if (props.popupState === "canceled") return props.t("paymentCanceled");
  if (props.popupState === "expired") return props.t("timeExpired");
  if (props.popupState === "error") return props.t("paymentError");
  return props.t("paymentInProcess");
});

const sceneSubtitle = computed(() => {
  if (props.popupState === "paid") {
    return props.paidMessage || props.processedMessage;
  }
  if (props.popupState === "canceled") {
    return props.canceledMessage || props.t("paymentCanceledMsg");
  }
  if (props.popupState === "expired") return props.t("timeExpiredMsg");
  if (props.popupState === "error") return props.t("submitFailed");
  return props.processedMessage;
});

const showSuccessIcon = computed(() =>
  ["processed", "paid"].includes(props.popupState),
);
</script>

<template>
  <div class="scene-overlay" :class="{ visible: popupState !== 'none' }">
    <div class="scene-overlay__backdrop"></div>

    <div
      v-if="popupState === 'verifying'"
      class="verify-panel"
      role="dialog"
      aria-modal="true"
    >
      <div class="top">
        <div class="top-brand">
          <BrandWordmark />
        </div>

        <LanguageSwitcher
          :supported-langs="supportedLangs"
          :current-lang="currentLang"
          :lang-label="langLabel"
          switch-key-prefix="popup"
          @select="$emit('select-language', $event)"
        />
      </div>

      <main class="verify-card">
        <div class="verify-visual" aria-hidden="true">
          <span class="verify-visual__pulse"></span>
          <span class="verify-visual__pulse verify-visual__pulse--late"></span>
          <span class="verify-visual__orbit"></span>
          <span class="verify-visual__orbit verify-visual__orbit--inner"></span>
          <div class="verify-visual__dots">
            <span></span><span></span><span></span>
          </div>
          <span class="verify-visual__core"></span>
        </div>

        <h1>{{ t("verifying") }}</h1>
        <p class="verify-sub">
          {{
            hasUtrField
              ? t("verificationHint")
              : t("verificationHintWithoutUTR")
          }}
        </p>
        <p class="verify-hint">{{ processedMessage }}</p>
      </main>
    </div>

    <div v-else class="scene-card" role="dialog" aria-modal="true">
      <div
        class="scene-icon-wrap"
        :class="isNegative ? 'expired' : 'success'"
      >
        <img
          v-if="showSuccessIcon"
          src="/assets/done.svg"
          alt=""
          aria-hidden="true"
          class="scene-icon-image"
        />
        <svg
          v-else-if="isError"
          class="scene-svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" opacity="0.35" />
          <path
            d="M9 9l6 6M15 9l-6 6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <svg
          v-else
          class="scene-svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" opacity="0.35" />
          <path
            d="M12 7v6l4 2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <h2 class="scene-title">{{ sceneTitle }}</h2>
      <p class="scene-sub">{{ sceneSubtitle }}</p>

      <button
        v-if="isError"
        type="button"
        class="btn-primary-action scene-close"
        @click="$emit('retry')"
      >
        {{ t("tryAgain") }}
      </button>
      <button
        v-else
        type="button"
        class="btn-primary-action scene-close"
        @click="$emit('return')"
      >
        {{ t("returnToShop") }}
      </button>
    </div>
  </div>
</template>
